import {
  Box,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { useEffect, useState } from "react";

import { EmailFlagsDisplay } from "../../../types/account";

interface EmailFlagsGridProps {
  emailFlagsDisplay: EmailFlagsDisplay | null;
  onChange: (newEmailFlagsDisplay: EmailFlagsDisplay) => void;
  disabled?: boolean;
}

interface EmailFeatureRow {
  id: keyof EmailFlagsDisplay;
  name: string;
  disableForUser?: boolean;
}

const featureRows: EmailFeatureRow[] = [
  { id: "topics", name: "Topics Discussed (Internal calls only)" },
  { id: "scorecard", name: "Scorecard", disableForUser: true },
  { id: "riskAnalysis", name: "Risk Analysis (Beta)", disableForUser: true },
  { id: "extendedSummary", name: "Extended Summary" },
  { id: "openConversationButton", name: "Open Conversation Button" }
];

export const EmailFlagsGrid = ({
  emailFlagsDisplay,
  onChange,
  disabled = false
}: EmailFlagsGridProps) => {
  const [localEmailFlags, setLocalEmailFlags] = useState<EmailFlagsDisplay | null>(null);

  useEffect(() => {
    if (emailFlagsDisplay) {
      setLocalEmailFlags(emailFlagsDisplay);
    }
  }, [emailFlagsDisplay]);

  if (!localEmailFlags) {
    return null;
  }

  const handleCheckboxChange = (
    featureId: keyof EmailFlagsDisplay,
    recipient: "user" | "bcc"
  ) => {
    const updatedFlags = {
      ...localEmailFlags,
      [featureId]: {
        ...localEmailFlags[featureId],
        [recipient]: !localEmailFlags[featureId][recipient]
      }
    };

    setLocalEmailFlags(updatedFlags);
    onChange(updatedFlags);
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" gutterBottom>
        Email Payload Configuration
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure what is included in the email payload for each recipient
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Feature</TableCell>
              <TableCell align="center">User</TableCell>
              <TableCell align="center">Central Inbox</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {featureRows.map((row) => (
              <TableRow key={row.id}>
                <TableCell component="th" scope="row">
                  {row.name}
                </TableCell>
                <TableCell align="center">
                  <Checkbox
                    checked={localEmailFlags[row.id].user}
                    onChange={() => handleCheckboxChange(row.id, "user")}
                    disabled={disabled || row.disableForUser}
                  />
                </TableCell>
                <TableCell align="center">
                  <Checkbox
                    checked={localEmailFlags[row.id].bcc}
                    onChange={() => handleCheckboxChange(row.id, "bcc")}
                    disabled={disabled}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
